// frontend/src/components/asset/SetDeclarationDeadlineModal.js
/**
 * Custodian sets the declaration deadline printed on an Ops-sourced notice.
 *
 * Ops does not supply this field. It drives the reminder sweep and is required
 * before particulars can be marked as furnished. Does not block the driver-
 * confirm round (that opens on ingest).
 *
 * Theme: Admin (red). One primary action (Von Restorff).
 */

import React, { useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import Modal from '../UI/Modal';
import DatePickerField from '../UI/DatePickerField';
import { DATE_PICKER_FORMAT, toLocalISODate } from '../../utils/dateUtils';
import { useSetOffenceDeclarationDeadline } from '../../hooks/useAssetInventoryData';

const SetDeclarationDeadlineModal = ({ isOpen, onClose, notice }) => {
  const [deadline, setDeadline] = useState(null);
  const mutation = useSetOffenceDeclarationDeadline();

  useEffect(() => {
    if (!isOpen || !notice) return;
    setDeadline(
      notice.declaration_deadline
        ? new Date(`${notice.declaration_deadline}T00:00:00`)
        : null
    );
  }, [isOpen, notice]);

  if (!notice) return null;

  const handleSave = async () => {
    if (!deadline) return;
    await mutation.mutateAsync({
      id: notice.id,
      declaration_deadline: toLocalISODate(deadline),
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Declaration Deadline"
      size="md"
      theme="admin"
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 rounded-xl border border-amber-200 bg-amber-50">
          <CalendarClock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 text-sm text-amber-900">
            <p className="font-medium">
              {notice.vehicle_number_raw} · {notice.notice_number}
            </p>
            <p className="text-amber-800 mt-0.5">
              Enter the deadline printed on the notice for furnishing driver
              particulars. Missing it is a separate offence for the company.
            </p>
          </div>
        </div>

        <div>
          <label className="form-label-admin">
            Declaration deadline <span className="text-red-600">*</span>
          </label>
          <DatePickerField
            selected={deadline}
            onChange={setDeadline}
            dateFormat={DATE_PICKER_FORMAT}
            placeholderText="Select date"
            theme="admin"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline flex-1"
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn btn-admin-primary flex-1"
            disabled={!deadline || mutation.isPending}
          >
            {mutation.isPending ? 'Saving…' : 'Save Deadline'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SetDeclarationDeadlineModal;
